import { RawMetricValueTypes } from '../metrics/types'
import { PlayerSummaryMetricKey } from '../sports/config'
import { sportTypes } from '../sports/data_types'
import { RawPlayerBreakdown } from './types'

// A map of accessors for one sport. Keys are constrained to metrics actually defined
// in config.json (PlayerSummaryMetricKey), so an accessor for a misspelled or
// non-existent metric is a compile error. Partial: a sport need not cover every metric.
type SportAccessors = Partial<Record<PlayerSummaryMetricKey, BreakdownAccessor>>

// An accessor reads one numeric metric value (or null) from a single raw breakdown
// entity. The same accessor runs over `totals` and over every player, so totals and
// per-player extraction can never drift apart.
export type BreakdownAccessor = (
  b: RawPlayerBreakdown
) => RawMetricValueTypes | null

// A guard gates a metric on category-level involvement. When it returns false the
// metric is emitted as null (e.g. recycle time for a player who never carried).
type Guard = (b: RawPlayerBreakdown) => boolean

const passGuard: Guard = (b) => parseInt(b.passes.totalInvolvements) > 0
const carryGuard: Guard = (b) => parseInt(b.dribbling.total) > 0

// Aggregate nodes carry a tally of contributing records; a tally of 0 means there were no
// records, so the accompanying value is a meaningless 0 and the metric reads null (renders '-')
// rather than a real zero. Most nodes tally in `count`, but the percentage node's `count` is the
// numerator (completions) — its record signal is `total` (attempts). Gate on either, so a real
// 0% (threw, completed nothing) still shows while a no-records node reads '-'.
const hasRecords = (node: { count: string; total?: string }) =>
  parseInt(node.count) > 0 || parseInt(node.total ?? '0') > 0

// Two helpers, split by the shape they read: `count` takes a scalar tally field, `stat` takes
// an aggregate stat node. The author names the raw field via a typed selector and the helper
// handles the parse + record/involvement gating, so the shape decides the rest — a selector
// aimed at the wrong helper is a compile error rather than a silent mis-parse.
//
// Selectors are intentionally NOT optional-chained: a missing node (backend contract
// drift) throws here and is caught by formatPlayerBreakdown's safeFormat — degrading the
// entity and warning — rather than silently coercing to NaN and hiding the drift.

// A tally of discrete things, so 0 is a real answer and is emitted as 0, not as absence.
const count =
  (sel: (b: RawPlayerBreakdown) => string, guard?: Guard): BreakdownAccessor =>
  (b) =>
    guard && !guard(b) ? null : parseInt(sel(b))

// Reads the value off an aggregate node — RawPlayerBreakdownAverage ({ total, count, value }),
// the max/longest nodes ({ value, count }) or the percentage node ({ count, total, value }) —
// which is why a no-records node reads as absence rather than a measured zero.
//
// Parses as a float because no such node is an integer: each carries either a measurement in
// SI base units (metres, m/s, rev/s) or a 0–1 fraction. The display unit conversion happens
// downstream in processMetric, so truncating here would shift the whole displayed number —
// 40.87 m truncated to 40 m renders as 43.7 yd instead of 44.7 yd.
const stat =
  (
    sel: (b: RawPlayerBreakdown) => { value: string; count: string },
    guard?: Guard
  ): BreakdownAccessor =>
  (b) => {
    if (guard && !guard(b)) return null
    const node = sel(b)
    return hasRecords(node) ? parseFloat(node.value) : null
  }

// Per-sport accessor maps, keyed by the camelCase metric key used in
// playerSummaryMetrics config (metrics/functions.ts looks up rawMetrics[config.key]).
const SOCCER_ACCESSORS: SportAccessors = {
  totalPasses: count((b) => b.passes.allPasses.total),
  // Soccer shows completions as a count (not a %): read the numerator, gated like totalPasses.
  totalPassCompletion: count((b) => b.passes.allPasses.completion.count),
  totalForwardPasses: count((b) => b.passes.forwardPasses.total),
  totalBackwardPasses: count((b) => b.passes.backwardPasses.total),
  totalSidewaysPasses: count((b) => b.passes.sidewaysPasses.total),
  totalShotsAtGoal: count((b) => b.shooting.total),
  totalGoalsScored: count((b) => b.shooting.totalGoals),
  averageRecycleTime: stat((b) => b.dribbling.averageRecycleTime, carryGuard)
}

const AMERICAN_FOOTBALL_ACCESSORS: SportAccessors = {
  totalPasses: count((b) => b.passes.allPasses.total),
  // Completion %: read the backend-derived fraction, which the formatter scales ×100.
  totalPassCompletion: stat((b) => b.passes.allPasses.completion),
  averageThrowDistance: stat(
    (b) => b.passes.allPasses.averageDistance,
    passGuard
  ),
  averageSpeed: stat((b) => b.passes.allPasses.averageSpeed, passGuard),
  averageSpin: stat((b) => b.passes.allPasses.averageSpin, passGuard),
  // Efficiency, like completion, is a 0–1 fraction rather than a physical measurement —
  // unitType 'percentage', scaled ×100 for display rather than unit-converted.
  averageEfficiency: stat(
    (b) => b.passes.allPasses.averageEfficiency,
    passGuard
  ),
  longestDistance: stat((b) => b.passes.allPasses.longestDistance, passGuard),
  averageReleaseVelocity: stat(
    (b) => b.passes.allPasses.averageReleaseVelocity,
    passGuard
  ),
  maxReleaseVelocity: stat(
    (b) => b.passes.allPasses.maxReleaseVelocity,
    passGuard
  ),
  maxSpin: stat((b) => b.passes.allPasses.maxSpin, passGuard),
  maxEfficiency: stat((b) => b.passes.allPasses.maxEfficiency, passGuard)
}

// Sport value -> accessor map. Sports without an entry produce no metrics, matching
// the previous behaviour where only soccer and american football had branches.
export const BREAKDOWN_ACCESSORS: Record<string, SportAccessors> = {
  [sportTypes.items.soccer.value]: SOCCER_ACCESSORS,
  [sportTypes.items.americanFootball.value]: AMERICAN_FOOTBALL_ACCESSORS,
  // Canadian football shares American football's pass-breakdown payload — the backend
  // routes both sports to the same aggregator (be--monorepo modules/sports/aggregators.go),
  // so the same accessor map extracts it.
  [sportTypes.items.canadianFootball.value]: AMERICAN_FOOTBALL_ACCESSORS
}
